import type { DashboardTab } from "@/types/dashboard";
import {
  ASSISTANT_PENDING_EVENT,
  ASSISTANT_PENDING_KEY,
  normalizeAssistantAction,
  normalizeAssistantTab,
  type AssistantUiAction
} from "./types";

export function queueAssistantPending(actions: AssistantUiAction[]): void {
  if (!actions.length) return;
  try {
    sessionStorage.setItem(ASSISTANT_PENDING_KEY, JSON.stringify(actions));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(ASSISTANT_PENDING_EVENT));
  window.setTimeout(() => window.dispatchEvent(new CustomEvent(ASSISTANT_PENDING_EVENT)), 400);
  window.setTimeout(() => window.dispatchEvent(new CustomEvent(ASSISTANT_PENDING_EVENT)), 900);
}

export function consumeAssistantPending(): AssistantUiAction[] {
  try {
    const raw = sessionStorage.getItem(ASSISTANT_PENDING_KEY);
    sessionStorage.removeItem(ASSISTANT_PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((a) => normalizeAssistantAction(a))
      .filter((a): a is AssistantUiAction => Boolean(a));
  } catch {
    return [];
  }
}

export function splitAssistantActions(actions: unknown[]): {
  tables: AssistantUiAction[];
  contentSearches: AssistantUiAction[];
  tabs: AssistantUiAction[];
  pending: AssistantUiAction[];
} {
  const tables: AssistantUiAction[] = [];
  const contentSearches: AssistantUiAction[] = [];
  const tabs: AssistantUiAction[] = [];
  const pending: AssistantUiAction[] = [];
  for (const raw of actions) {
    const a = normalizeAssistantAction(raw);
    if (!a) continue;
    if (a.type === "show_table") {
      tables.push(a);
      continue;
    }
    if (a.type === "show_page_content_search") {
      contentSearches.push(a);
      continue;
    }
    if (a.type === "set_tab") {
      tabs.push(a);
      continue;
    }
    pending.push(a);
  }
  return { tables, contentSearches, tabs, pending };
}

const TAB_FOR_ACTION: Partial<Record<AssistantUiAction["type"], DashboardTab>> = {
  open_lead_by_id: "leads",
  open_lead_by_identity: "leads",
  open_request_log_entry: "request-log",
  open_page_stats: "site-structure",
  open_recording: "recordings"
};

export function executeAssistantUiActions(
  actions: unknown[],
  setActiveTab: (tab: DashboardTab) => void
): { tables: AssistantUiAction[]; contentSearches: AssistantUiAction[] } {
  const { tables, contentSearches, tabs, pending } = splitAssistantActions(actions);
  for (const a of tabs) {
    if (a.type === "set_tab" && a.tab) {
      setActiveTab(normalizeAssistantTab(a.tab));
    }
  }
  if (pending.length) {
    if (!tabs.length) {
      for (const a of pending) {
        const inferred = TAB_FOR_ACTION[a.type];
        if (inferred) {
          setActiveTab(inferred);
          break;
        }
      }
    }
    queueAssistantPending(pending);
  }
  return { tables, contentSearches };
}
